import React, { useEffect, useMemo, useState } from 'react';
import {
  BarList, Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs,
  TextInput, fmtCell, fmtDate, panelPost, useAdminGet,
} from './kit';

/**
 * Database group — a live explorer for the AWS Postgres the whole product now
 * reads from (technologies, researchers, tech managers, users, search history).
 *
 * Everything here is read-only: the browser and the console both run inside a
 * read-only transaction server-side, and secret-looking columns come back as
 * "<redacted>" before the response leaves the backend.
 *
 * The one screen worth explaining is "University mapping": people keep looking
 * for a universities table and there isn't one. The pairing lives on the corpus
 * rows themselves (technologies.utech_university_id + university_name), with a
 * second hardcoded copy in combined_rag_system.py — so that tab leads with the
 * explanation and then shows both copies side by side, plus the drift between
 * them.
 */

const TABS = ['Overview', 'Tables', 'Schema', 'Browse rows', 'Query console',
  'University mapping'];

const DatabasePanel: React.FC = () => {
  const [tab, setTab] = useState(TABS[0]);
  // Shared so "Open" on the Tables list can hand a table to the Schema tab.
  const [table, setTable] = useState('');

  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={TABS} active={tab} onChange={setTab} />
      {tab === 'Overview' && <Overview />}
      {tab === 'Tables' && (
        <Tables onOpen={t => { setTable(t); setTab('Schema'); }} />
      )}
      {tab === 'Schema' && <Schema table={table} onTable={setTable} />}
      {tab === 'Browse rows' && <Browse table={table} onTable={setTable} />}
      {tab === 'Query console' && <QueryConsole />}
      {tab === 'University mapping' && <UniversityMapping />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-1 text-sm">
    <span className="text-grayscale-1100 whitespace-nowrap">{label}</span>
    <span className="text-grayscale-1300 font-medium text-right break-all">{value ?? '—'}</span>
  </div>
);

const n = (v: any): string =>
  typeof v === 'number' ? v.toLocaleString() : (v === null || v === undefined ? '—' : String(v));

/** Picker shared by the Schema and Browse tabs — owns its own table listing. */
const TablePicker: React.FC<{ value: string; onChange: (t: string) => void }> =
  ({ value, onChange }) => {
  const list = useAdminGet('db', 'tables');
  const names: string[] = (list.data?.rows || []).map((r: any) => r.table);
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-grayscale-1100">Table</span>
      <Select value={value} onChange={onChange} options={['', ...names]}
        labels={{ '': list.loading ? 'Loading tables…' : 'Choose a table…' }} />
      {list.error && <span className="text-xs text-red-600">{list.error}</span>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 1. Overview — who this database is, how big it is, and whether the local
//    SQLite copy some admin screens still read has drifted away from it.
// ---------------------------------------------------------------------------
const StatusPill: React.FC<{ value: string }> = ({ value }) => {
  const v = (value || '').toLowerCase();
  const look = v === 'in sync'
    ? 'text-green-700 bg-green-50 border-green-200'
    : v === 'drifted'
      ? 'text-red-700 bg-red-50 border-red-300 font-bold'
      : 'text-amber-700 bg-amber-50 border-amber-200';
  return <span className={`text-xs rounded-full px-2 py-0.5 border ${look}`}>{value || '—'}</span>;
};

const Overview: React.FC = () => {
  const ov = useAdminGet('db', 'overview');
  const corpus = useAdminGet('db', 'corpus_status');
  const o = ov.data || {};
  const c = corpus.data || {};
  const biggest: any[] = o.biggest_tables || [];
  const corpusTables: any[] = c.tables || [];
  const drifted = corpusTables.filter(t => t.status === 'DRIFTED');

  return (
    <div className="flex flex-col gap-4">
      {ov.error && <Msg kind="error">{ov.error}</Msg>}
      {ov.loading ? <Loading /> : (
        <>
          <KPIRow>
            <KPI label="Tables" value={n(o.tables)} />
            <KPI label="Views" value={n(o.views)} />
            <KPI label="Indexes" value={n(o.indexes)} />
            <KPI label="Connections" value={n(o.connections)} hint="open right now" />
            <KPI label="Size on disk" value={o.size ?? '—'} />
          </KPIRow>
          <Section title="Database identity" right={<Btn small onClick={ov.reload}>Refresh</Btn>}>
            <div className="flex flex-col divide-y divide-grayscale-200">
              <Fact label="Database" value={o.database} />
              <Fact label="Host" value={o.host} />
              <Fact label="Version" value={o.version} />
              <Fact label="Connected as" value={o.role} />
              <Fact label="Total size" value={o.size} />
            </div>
          </Section>
          <Section title="Biggest tables by size on disk">
            <p className="text-xs text-grayscale-1100 -mt-1">
              Bars are total relation size in bytes (heap + indexes + TOAST); the pretty
              size is on each label.
            </p>
            <BarList items={biggest.map(t => ({
              label: `${t.table} · ${t.size}`,
              value: Number(t.size_bytes) || 0,
            }))} />
          </Section>
        </>
      )}

      {corpus.error && <Msg kind="error">{corpus.error}</Msg>}
      {corpus.loading ? <Loading label="Checking the corpus backend…" /> : (
        <Section title="Corpus backend" right={<Btn small onClick={corpus.reload}>Refresh</Btn>}>
          {!!drifted.length && (
            <Msg kind="error">
              {drifted.length} table{drifted.length === 1 ? ' has' : 's have'} DRIFTED —
              the SQLite copy no longer matches Postgres, so any admin screen still
              reading the copy ({drifted.map(t => t.table).join(', ')}) is reporting
              numbers the product does not use.
            </Msg>
          )}
          <div className="flex flex-col divide-y divide-grayscale-200">
            <Fact label="Corpus backend in use" value={
              <span className="font-mono">{c.corpus_backend ?? '—'}</span>} />
            <Fact label="Postgres host" value={
              <span className="font-mono">{c.postgres_host ?? '—'}</span>} />
            <Fact label="SQLite copy" value={
              <span className="font-mono">{c.sqlite_path ?? '—'}</span>} />
            <Fact label="SQLite size" value={
              c.sqlite_size_mb === null || c.sqlite_size_mb === undefined
                ? '—' : `${c.sqlite_size_mb} MB`} />
          </div>
          <DataTable rows={corpusTables} csvName="corpus_status"
            columns={['table', 'postgres', 'sqlite_copy', 'status']}
            labels={{ postgres: 'Postgres rows', sqlite_copy: 'SQLite copy', status: 'Status' }}
            empty="The backend reported no corpus tables."
            render={{ status: r => <StatusPill value={r.status} /> }} />
          {c.note && <p className="text-xs text-grayscale-1100">{c.note}</p>}
        </Section>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Tables — the whole inventory, searched server-side.
// ---------------------------------------------------------------------------
const TABLE_COLS = ['table', 'rows', 'size', 'columns', 'inserts', 'updates', 'deletes',
  'last_autovacuum', 'last_analyze'];
const SORT_KEYS = ['size_bytes', 'table', 'rows', 'columns', 'inserts', 'updates', 'deletes',
  'last_autovacuum', 'last_analyze'];
const SORT_LABELS: Record<string, string> = {
  size_bytes: 'Size', table: 'Name', rows: 'Rows (est.)', columns: 'Columns',
  inserts: 'Inserts', updates: 'Updates', deletes: 'Deletes',
  last_autovacuum: 'Last autovacuum', last_analyze: 'Last analyze',
};

const Tables: React.FC<{ onOpen: (t: string) => void }> = ({ onOpen }) => {
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState('size_bytes');
  const [descending, setDescending] = useState(true);
  const list = useAdminGet('db', 'tables', { q }, [q]);
  const rows: any[] = list.data?.rows || [];

  const sorted = useMemo(() => [...rows].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv : String(av).localeCompare(String(bv));
    return descending ? -cmp : cmp;
  }), [rows, sortKey, descending]);

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Tables" value={n(list.data?.total ?? rows.length)}
          hint={q.trim() ? 'matching the search' : undefined} />
        <KPI label="Rows (estimated)"
          value={n(rows.reduce((a, r) => a + (Number(r.rows) || 0), 0))} />
        <KPI label="Size shown"
          value={`${Math.round(rows.reduce((a, r) => a + (Number(r.size_bytes) || 0), 0)
            / 1048576).toLocaleString()} MB`} />
      </KPIRow>
      <Section title="Every table in the database"
        right={<div className="flex items-center gap-2 flex-wrap">
          <TextInput value={q} onChange={setQ} placeholder="Search table names…" />
          <Btn small onClick={list.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          The search is applied by the server across all tables, not just the ones on
          screen. {list.data?.note || 'Row counts are the planner’s live estimate.'} Open a
          table to see its real schema, or take an exact count there.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">Sort by</span>
          <Select value={sortKey} onChange={setSortKey} options={SORT_KEYS} labels={SORT_LABELS} />
          <Btn small onClick={() => setDescending(d => !d)}>
            {descending ? 'Descending ↓' : 'Ascending ↑'}
          </Btn>
        </div>
        {list.error && <Msg kind="error">{list.error}</Msg>}
        {list.loading ? <Loading /> : (
          <DataTable rows={sorted} columns={TABLE_COLS} csvName="db_tables" maxHeight="36rem"
            labels={{ rows: 'Rows (est.)', size: 'Size', columns: 'Columns' }}
            empty={q.trim() ? `No table matches “${q.trim()}”.` : 'No tables reported.'}
            render={{
              table: r => <span className="font-mono">{r.table}</span>,
              last_autovacuum: r => <>{fmtDate(r.last_autovacuum)}</>,
              last_analyze: r => <>{fmtDate(r.last_analyze)}</>,
            }}
            actions={r => <Btn small onClick={() => onOpen(r.table)}>Open</Btn>} />
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 3. Schema — the real definition of one table.
// ---------------------------------------------------------------------------
const Schema: React.FC<{ table: string; onTable: (t: string) => void }> = ({ table, onTable }) => (
  <div className="flex flex-col gap-4">
    <Section title="Table schema">
      <TablePicker value={table} onChange={onTable} />
      {!table && (
        <p className="text-sm text-grayscale-1100">
          Pick a table to see its columns, indexes, foreign keys and size — or press
          “Open” on any row of the Tables tab.
        </p>
      )}
    </Section>
    {table && <SchemaFor key={table} table={table} />}
  </div>
);

const SchemaFor: React.FC<{ table: string }> = ({ table }) => {
  // exact_count runs a real COUNT(*), which is slow on the big tables — it is
  // only ever sent because someone pressed the button.
  const [exact, setExact] = useState(false);
  const d = useAdminGet('db', 'table', { table, exact_count: exact ? '1' : '' },
    [table, exact]);
  const t = d.data || {};
  const columns: any[] = t.columns || [];
  const indexes: any[] = t.indexes || [];
  const fkOut: any[] = t.foreign_keys_out || [];
  const refBy: any[] = t.referenced_by || [];
  const size = t.size || {};

  const colRows = columns.map(c => ({
    position: c.ordinal_position,
    column: c.column_name,
    type: c.data_type,
    nullable: c.is_nullable === 'YES' ? 'NULL' : 'NOT NULL',
    default: c.column_default,
    pk: c.primary_key ? 'PK' : '',
    max_length: c.character_maximum_length,
    precision: c.numeric_precision,
  }));

  if (d.loading) return <Loading label={`Reading the schema of ${table}…`} />;
  if (d.error) return <Msg kind="error">{d.error}</Msg>;

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label="Columns" value={columns.length} />
        <KPI label="Indexes" value={indexes.length} />
        <KPI label="Rows (estimated)" value={n(size.estimated_rows)} hint="planner estimate" />
        <KPI label="Exact rows"
          value={t.exact_rows === null || t.exact_rows === undefined ? '—' : n(t.exact_rows)}
          hint={t.exact_rows === null || t.exact_rows === undefined
            ? 'not counted yet' : 'real COUNT(*)'} />
        <KPI label="Total size" value={size.total ?? '—'} hint={size.heap ? `heap ${size.heap}` : undefined} />
      </KPIRow>
      <Section title={`Columns — ${table}`}
        right={<div className="flex items-center gap-2">
          <Btn small onClick={() => (exact ? d.reload() : setExact(true))}>
            Exact count
          </Btn>
          <Btn small onClick={d.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Primary key:{' '}
          <span className="font-mono">{(t.primary_key || []).join(', ') || 'none'}</span>.
          “Exact count” runs a real COUNT(*) — accurate, and slow on the big tables,
          which is why it is never automatic.
        </p>
        <DataTable rows={colRows} csvName={`schema_${table}`} maxHeight="32rem"
          columns={['position', 'column', 'type', 'nullable', 'default', 'pk', 'max_length', 'precision']}
          labels={{ position: '#', pk: 'Key', max_length: 'Max length', precision: 'Precision' }}
          empty="This table reported no columns."
          render={{
            column: r => <span className="font-mono">{r.column}</span>,
            default: r => r.default
              ? <span className="font-mono text-xs">{r.default}</span>
              : <span className="text-grayscale-1100">—</span>,
            pk: r => r.pk
              ? <span className="text-xs rounded-full px-2 py-0.5 border text-primary-700 bg-primary-100 border-primary-300">PK</span>
              : <span className="text-grayscale-1100">—</span>,
          }} />
      </Section>
      <div className="grid gap-4 md:grid-cols-2">
        <Section title={`Foreign keys out (${fkOut.length})`}>
          <DataTable rows={fkOut} maxHeight="18rem"
            columns={['column', 'references_table', 'references_column', 'name']}
            labels={{ references_table: 'References table', references_column: 'Column', name: 'Constraint' }}
            empty="This table references no other table." />
        </Section>
        <Section title={`Referenced by (${refBy.length})`}>
          <DataTable rows={refBy} maxHeight="18rem"
            columns={['from_table', 'name']}
            labels={{ from_table: 'From table', name: 'Constraint' }}
            empty="No table points at this one." />
        </Section>
      </div>
      <Section title={`Indexes (${indexes.length})`}>
        <DataTable rows={indexes} maxHeight="22rem" csvName={`indexes_${table}`}
          columns={['name', 'definition']}
          labels={{ name: 'Index', definition: 'Definition' }}
          empty="This table has no indexes."
          render={{
            name: r => <span className="font-mono">{r.name}</span>,
            definition: r => (
              <span className="font-mono text-xs" title={r.definition}>{r.definition}</span>
            ),
          }} />
      </Section>
      <Section title="Size">
        <div className="flex flex-col divide-y divide-grayscale-200">
          <Fact label="Total (heap + indexes + TOAST)" value={size.total} />
          <Fact label="Heap only" value={size.heap} />
          <Fact label="Rows (planner estimate)" value={n(size.estimated_rows)} />
          <Fact label="Rows (exact)" value={
            t.exact_rows === null || t.exact_rows === undefined
              ? 'not counted — press “Exact count”' : n(t.exact_rows)} />
        </div>
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 4. Browse rows — the data browser: page, order, one filter, row drawer.
// ---------------------------------------------------------------------------
const Browse: React.FC<{ table: string; onTable: (t: string) => void }> = ({ table, onTable }) => (
  <div className="flex flex-col gap-4">
    <Section title="Browse rows">
      <TablePicker value={table} onChange={onTable} />
      {!table && (
        <p className="text-sm text-grayscale-1100">
          Pick a table to page through its rows. Everything here runs in a read-only
          transaction and secret-looking columns are redacted by the server.
        </p>
      )}
    </Section>
    {table && <RowBrowser key={table} table={table} />}
  </div>
);

const PAGE_SIZES = ['25', '50', '100', '500'];
const NO_VALUE_OPS = ['is null', 'is not null'];

/** Pretty-print a JSON-ish value; null when it is not JSON. */
const asJson = (v: any): string | null => {
  if (v && typeof v === 'object') return JSON.stringify(v, null, 2);
  if (typeof v === 'string') {
    const s = v.trim();
    if ((s.startsWith('{') && s.endsWith('}')) || (s.startsWith('[') && s.endsWith(']'))) {
      try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return null; }
    }
  }
  return null;
};

const RowBrowser: React.FC<{ table: string }> = ({ table }) => {
  const [limit, setLimit] = useState('50');
  const [offset, setOffset] = useState(0);
  const [orderBy, setOrderBy] = useState('');
  const [descending, setDescending] = useState(true);
  // Draft filter vs applied filter: the server rejects a value-taking operator
  // with an empty value, so nothing is sent until "Apply".
  const [fcol, setFcol] = useState('');
  const [fop, setFop] = useState('contains');
  const [fval, setFval] = useState('');
  const [applied, setApplied] = useState<{ col: string; op: string; val: string }>(
    { col: '', op: '', val: '' });
  const [open, setOpen] = useState<Record<string, any> | null>(null);

  const d = useAdminGet('db', 'rows', {
    table, limit, offset,
    order_by: orderBy,
    desc: descending ? '1' : '',
    filter_column: applied.col,
    filter_op: applied.op,
    filter_value: applied.val,
  }, [table, limit, offset, orderBy, descending, applied.col, applied.op, applied.val]);

  // The column list, the filterable columns and the operator vocabulary all come
  // back with the rows — keep the last good copy so the controls do not blink
  // away while the next page loads.
  const [meta, setMeta] = useState<{ columns: string[]; filterColumns: string[]; operators: string[] }>(
    { columns: [], filterColumns: [], operators: [] });
  useEffect(() => {
    if (!d.data) return;
    setMeta({
      columns: d.data.columns || [],
      filterColumns: d.data.filter_columns || [],
      operators: d.data.operators || [],
    });
  }, [d.data]);

  const columns: string[] = d.data?.columns || meta.columns;
  const raw: any[][] = d.data?.rows || [];
  // The handler returns rows as arrays aligned to `columns`, not objects.
  const rows = useMemo(
    () => raw.map(r => Object.fromEntries(columns.map((c, i) => [c, r[i]]))),
    [raw, columns]);
  const total: number = d.data?.total ?? 0;
  const srvLimit: number = d.data?.limit ?? Number(limit);
  const srvOffset: number = d.data?.offset ?? offset;
  const redacted: string[] = d.data?.redacted_columns || [];
  const from = total ? srvOffset + 1 : 0;
  const to = srvOffset + rows.length;
  const hasFilter = !!applied.col;

  const setPageSize = (v: string) => { setLimit(v); setOffset(0); };
  const applyFilter = () => { setApplied({ col: fcol, op: fop, val: fval }); setOffset(0); };
  const clearFilter = () => {
    setFcol(''); setFop('contains'); setFval('');
    setApplied({ col: '', op: '', val: '' }); setOffset(0);
  };
  const needsValue = !NO_VALUE_OPS.includes(fop);
  const canApply = !!fcol && (!needsValue || !!fval.trim());

  return (
    <div className="flex flex-col gap-4">
      <KPIRow>
        <KPI label={hasFilter ? 'Rows matching' : 'Rows in table'} value={n(total)}
          hint={hasFilter ? 'after the filter' : 'exact COUNT(*) of this query'} />
        <KPI label="Showing" value={total ? `${from.toLocaleString()}–${to.toLocaleString()}` : '0'}
          hint={`page size ${srvLimit}`} />
        <KPI label="Columns" value={columns.length} />
      </KPIRow>
      <Section title={`Rows — ${table}`}
        right={<Btn small onClick={d.reload}>Refresh</Btn>}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">Page size</span>
          <Select value={limit} onChange={setPageSize} options={PAGE_SIZES} />
          <span className="text-xs text-grayscale-1100 ml-2">Order by</span>
          <Select value={orderBy} onChange={v => { setOrderBy(v); setOffset(0); }}
            options={['', ...(meta.columns.length ? meta.columns : columns)]}
            labels={{ '': 'Server default' }} />
          <Btn small onClick={() => { setDescending(v => !v); setOffset(0); }}>
            {descending ? 'Descending ↓' : 'Ascending ↑'}
          </Btn>
          {!orderBy && (
            <span className="text-xs text-grayscale-1100">
              (default: newest by created_at / updated_at / id)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-grayscale-1100">Filter</span>
          <Select value={fcol} onChange={setFcol} options={['', ...meta.filterColumns]}
            labels={{ '': 'No filter' }} />
          <Select value={fop} onChange={setFop}
            options={meta.operators.length ? meta.operators : ['contains']} />
          {needsValue && <TextInput value={fval} onChange={setFval} placeholder="Value…" />}
          <Btn small kind="primary" disabled={!canApply} onClick={applyFilter}>Apply</Btn>
          {hasFilter && <Btn small onClick={clearFilter}>Clear</Btn>}
          {!!fcol && !canApply && (
            <span className="text-xs text-amber-700">Give a value to filter on.</span>
          )}
        </div>
        {hasFilter && (
          <p className="text-xs text-grayscale-1100">
            Filtering <span className="font-mono">{applied.col}</span>{' '}
            <span className="font-mono">{applied.op}</span>
            {NO_VALUE_OPS.includes(applied.op) ? '' : ` “${applied.val}”`} — the count above
            is of matching rows, not of the whole table.
          </p>
        )}
        {!!redacted.length && (
          <p className="text-xs text-amber-700">
            Redacted {redacted.length === 1 ? 'column' : 'columns'}:{' '}
            <span className="font-mono">{redacted.join(', ')}</span> — the server replaces
            secret values with <span className="font-mono">&lt;redacted&gt;</span> before the
            response leaves it. Every other column is the real value.
          </p>
        )}
        {d.error && <Msg kind="error">{d.error}</Msg>}
        <div className="flex items-center gap-2 flex-wrap">
          <Btn small disabled={srvOffset <= 0 || d.loading}
            onClick={() => setOffset(Math.max(0, offset - Number(limit)))}>← Previous</Btn>
          <Btn small disabled={to >= total || d.loading}
            onClick={() => setOffset(offset + Number(limit))}>Next →</Btn>
          <span className="text-xs text-grayscale-1100">
            Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
          </span>
        </div>
        {d.loading ? <Loading /> : (
          <DataTable rows={rows} columns={columns} maxHeight="34rem"
            csvName={`rows_${table}`}
            empty={hasFilter ? 'No row matches this filter.' : 'This table is empty.'}
            actions={r => <Btn small onClick={() => setOpen(r)}>View</Btn>} />
        )}
        <p className="text-xs text-grayscale-1100">
          CSV exports the rows on this page only — page through, or narrow with a
          filter, to export the rest.
        </p>
      </Section>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={() => setOpen(null)}>
          <div className="bg-grayscale-100 w-full max-w-2xl h-full overflow-y-auto p-6 flex flex-col gap-4"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-grayscale-1300 font-grotesk">
                  {table}
                </h2>
                <div className="text-xs text-grayscale-1100">
                  One row, every column as stored.
                </div>
              </div>
              <Btn onClick={() => setOpen(null)}>Close</Btn>
            </div>
            <Section title="Row">
              <div className="flex flex-col divide-y divide-grayscale-200">
                {columns.map(c => {
                  const v = open[c];
                  const json = asJson(v);
                  return (
                    <div key={c} className="py-2 flex flex-col gap-1">
                      <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 font-mono">
                        {c}
                        {redacted.includes(c) && (
                          <span className="ml-2 normal-case tracking-normal text-amber-700">
                            redacted server-side
                          </span>
                        )}
                      </div>
                      {json ? (
                        <pre className="text-xs text-grayscale-1300 bg-white border border-grayscale-300 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-words">
                          {json}
                        </pre>
                      ) : (
                        <div className="text-sm text-grayscale-1300 whitespace-pre-wrap break-words">
                          {fmtCell(c, v)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </Section>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// 5. Query console — POST system/sql, the same read-only path the System panel
//    uses, with starter queries written for THIS schema.
// ---------------------------------------------------------------------------
const PRESETS: { label: string; query: string }[] = [
  {
    label: 'Technologies per university',
    query: 'SELECT university_name, utech_university_id, COUNT(*) AS technologies\n'
      + 'FROM technologies\nGROUP BY university_name, utech_university_id\n'
      + 'ORDER BY technologies DESC',
  },
  {
    label: 'Users created per month',
    query: "SELECT date_trunc('month', created_at)::date AS month, COUNT(*) AS users\n"
      + 'FROM users\nGROUP BY 1\nORDER BY 1 DESC',
  },
  {
    label: 'Searches with no results',
    query: 'SELECT query, total, created_at\nFROM user_direct_search_history\n'
      + 'WHERE total = 0\nORDER BY created_at DESC\nLIMIT 100',
  },
  {
    label: 'Biggest tables',
    query: 'SELECT relname AS table_name,\n'
      + '       pg_size_pretty(pg_total_relation_size(relid)) AS total_size,\n'
      + '       n_live_tup AS estimated_rows\n'
      + 'FROM pg_stat_user_tables\nORDER BY pg_total_relation_size(relid) DESC\nLIMIT 20',
  },
];

const QueryConsole: React.FC = () => {
  const [query, setQuery] = useState(PRESETS[0].query);
  const [result, setResult] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (sql: string) => {
    setBusy(true); setErr(''); setResult(null);
    const { data, error } = await panelPost('system', 'sql', { query: sql });
    setBusy(false);
    if (error) setErr(error);
    else setResult(data);
  };

  // SELECT a, a would collapse into one property, so repeated names are suffixed.
  const cols: string[] = useMemo(() => {
    if (!result?.columns) return [];
    const seen: Record<string, number> = {};
    return result.columns.map((c: string) => {
      seen[c] = (seen[c] || 0) + 1;
      return seen[c] > 1 ? `${c} (${seen[c]})` : c;
    });
  }, [result]);
  const rows: any[] = useMemo(() => (result?.rows || []).map(
    (r: any[]) => Object.fromEntries(cols.map((c, i) => [c, r[i]]))), [result, cols]);
  const redacted: string[] = result?.redacted_columns || [];

  return (
    <Section title="SQL console — Postgres, read-only">
      <p className="text-xs text-grayscale-1100 -mt-1">
        One SELECT/WITH statement, run in a read-only transaction, capped at 500 rows,
        with secret-looking columns redacted before the result leaves the server. Writes
        are rejected by Postgres itself, not just by the input check.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-grayscale-1100">Starter queries:</span>
        {PRESETS.map(p => (
          <Btn key={p.label} small onClick={() => { setQuery(p.query); run(p.query); }}>
            {p.label}
          </Btn>
        ))}
      </div>
      <textarea value={query} rows={7} onChange={e => setQuery(e.target.value)}
        className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm font-mono"
        spellCheck={false} />
      <div className="flex items-center gap-2 flex-wrap">
        <Btn kind="primary" disabled={busy || !query.trim()} onClick={() => run(query)}>
          {busy ? 'Running…' : 'Run query'}
        </Btn>
        {result && (
          <span className="text-xs text-grayscale-1100">{rows.length} row(s)</span>
        )}
        {result?.truncated && (
          <span className="text-xs text-amber-700">
            Truncated at 500 rows — add a LIMIT, aggregate, or narrow the query to see the rest.
          </span>
        )}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      {!!redacted.length && (
        <p className="text-xs text-amber-700">
          Redacted {redacted.length === 1 ? 'column' : 'columns'}:{' '}
          <span className="font-mono">{redacted.join(', ')}</span> — replaced with{' '}
          <span className="font-mono">&lt;redacted&gt;</span> server-side, so this console
          never carries a password hash or a live token into the browser.
        </p>
      )}
      {result && (
        <DataTable rows={rows} columns={cols} csvName="db_query_result" searchable
          maxHeight="34rem" empty="Query ran and returned no rows." />
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// 6. University mapping — the answer to "where is this stored".
// ---------------------------------------------------------------------------
const MAPPING_COLS = ['code', 'short_code', 'name', 'country', 'technologies', 'researchers',
  'tech_managers', 'distinct_tto_emails', 'last_updated'];

/** The three names one university goes by, side by side with the addresses
 *  that reach it. They live in three different places and are used on three
 *  different screens, which is why they drift without anyone noticing. */
const NAMES_COLS = ['code', 'website_label', 'name', 'tto_name', 'common_email',
  'tto_email', 'tto_office_name', 'tto_office_emails', 'country', 'technologies'];

const UniversityMapping: React.FC = () => {
  const m = useAdminGet('db', 'university_mapping');
  const [showCode, setShowCode] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const saveLabel = async (code: string, label: string) => {
    setBusy(true);
    const r = await panelPost('db', 'set_university_label', { code, label });
    setBusy(false);
    setEditing(null);
    if (!r.error) m.reload();
  };
  const d = m.data || {};
  const live: any[] = d.live || [];
  const hardcoded: any[] = d.hardcoded || [];
  const drift: any[] = d.drift || [];
  const storage = d.storage || {};
  const totals = d.totals || {};

  return (
    <div className="flex flex-col gap-4">
      {m.error && <Msg kind="error">{m.error}</Msg>}
      {m.loading ? <Loading /> : (
        <>
          <Section title="Where this mapping is stored"
            right={<Btn small onClick={m.reload}>Refresh</Btn>}>
            <p className="text-sm text-grayscale-1300">
              {storage.explanation || 'No table stores this mapping.'}
            </p>
            <div className="flex flex-col divide-y divide-grayscale-200">
              <Fact label="Mapping table" value={
                storage.table
                  ? <span className="font-mono">{storage.table}</span>
                  : <span className="text-amber-700">none — there is no universities table</span>} />
              <Fact label="Source of truth" value={
                <span className="font-mono">{storage.authority ?? '—'}</span>} />
              <Fact label="Second copy in code" value={
                <span className="font-mono">{storage.code_copy ?? '—'}</span>} />
            </div>
          </Section>
          <KPIRow>
            <KPI label="Universities in the corpus" value={n(totals.universities)} />
            <KPI label="Hardcoded entries" value={n(totals.hardcoded_entries)}
              hint="in combined_rag_system.py" />
            <KPI label="Technologies without a code" value={n(totals.technologies_without_a_code)}
              hint={totals.technologies_without_a_code ? 'these resolve to no university' : 'every row is coded'} />
          </KPIRow>
          {!!drift.length && (
            <Section title={`Drift between the corpus and the hardcoded copy (${drift.length})`}>
              <div className="flex flex-col gap-2">
                {drift.map((x, i) => (
                  <div key={`${x.code}-${i}`}
                       className="text-sm rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                    <span className="font-mono font-bold">{x.code}</span>
                    <span className="ml-2 text-xs uppercase tracking-wide">{x.issue}</span>
                    <div className="text-sm mt-0.5">{x.detail}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}
          <Section title={`The three names — ${live.length} universities`}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              One university, three names, three screens. <b>Website label</b> is the short
              name for the homepage university ticker and anywhere the full name is too long
              (&quot;MIT&quot;, &quot;IIT Bombay&quot;), and the {'{university}'} part of its public page
              URLs; it has no column in the corpus, so it is stored here. The homepage ticker
              shows a university only when it has a label AND its carousel switch is on
              (Universities tab or Admin → Tech URL pages → Labels &amp; carousel). Saving or clearing one
              rebuilds the homepage doc in the background (about 20 seconds), and the homepage
              shows the change on its next load after that. Elsewhere a university without one
              simply keeps its full name.
              <b>DB name</b> (
              <span className="font-mono">technologies.university_name</span>) drives the tech
              card and the search filters. <b>TTO name</b> (
              <span className="font-mono">tech_manager_default→name</span>) is the scraped
              fallback for Contact University; the <b>curated TTO office</b> (
              <span className="font-mono">tto_office_details</span>, from the Global
              Universities sheet) takes precedence when it is set. Blank cells and a
              person&apos;s name where an office belongs are the things worth fixing.
            </p>
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                {live.filter((u: any) => !u.website_label).length} without a website label
              </span>
              <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                {live.filter((u: any) => !u.tto_name).length} without a TTO name
              </span>
              <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                {live.filter((u: any) => !u.tto_office_name).length} without a curated TTO office
              </span>
              <span className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                {live.filter((u: any) => (u.tto_name_variants || 0) > 1).length} whose TTO name
                disagrees with itself across their own technologies
              </span>
            </div>
            <DataTable rows={live} columns={NAMES_COLS} searchable csvName="university_names_and_emails"
              maxHeight="34rem"
              labels={{ code: 'UTECH code', website_label: 'Website label (D)', name: 'DB name (C)',
                tto_name: 'TTO name (O)', common_email: 'Common email', tto_email: 'TTO email',
                tto_office_name: 'TTO office (curated)',
                tto_office_emails: 'TTO office emails (curated)',
                technologies: 'Techs' }}
              empty="No university codes found in the corpus."
              render={{
                code: r => <span className="font-mono">{r.code}</span>,
                website_label: r => (editing === r.code ? (
                  <span className="inline-flex items-center gap-1">
                    <input autoFocus value={draft} onChange={e => setDraft(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') saveLabel(r.code, draft);
                        if (e.key === 'Escape') setEditing(null);
                      }}
                      className="h-7 px-1.5 w-32 rounded border-[0.5px] border-primary-300 text-xs" />
                    <Btn small disabled={busy} onClick={() => saveLabel(r.code, draft)}>Save</Btn>
                  </span>
                ) : (
                  <button type="button"
                    onClick={() => { setEditing(r.code); setDraft(r.website_label || ''); }}
                    title={r.label_source === 'seed'
                      ? 'Carried over from the landing page — click to make it official. The homepage ticker also needs the carousel switch (Universities tab)'
                      : r.website_label ? 'Short name used on the ticker and in page URLs — click to edit'
                      : 'Not set — click to add'}
                    className={`underline underline-offset-2 ${r.website_label
                      ? (r.label_source === 'seed' ? 'text-grayscale-1100 italic' : '')
                      : 'text-amber-700'}`}>
                    {r.website_label || 'set…'}
                  </button>
                )),
                tto_name: r => (
                  <span className={r.tto_name ? '' : 'text-amber-700'}>
                    {r.tto_name || 'missing'}
                    {(r.tto_name_variants || 0) > 1 && (
                      <span className="text-amber-700"> · {r.tto_name_variants} variants</span>
                    )}
                  </span>
                ),
                // The curated office comes from technologies.tto_office_details, loaded
                // from the Global Universities sheet. NULL means nobody has curated it
                // yet, which is the gap worth closing, so it is flagged like the others.
                tto_office_name: r => (
                  <span className={r.tto_office_name ? '' : 'text-amber-700'}
                    title={r.tto_office_source ? `Source: ${r.tto_office_source}` : undefined}>
                    {r.tto_office_name || 'not curated'}
                  </span>
                ),
                // Always an array in the curated shape; join it so the cell reads like
                // the legacy common_email column instead of a JSON literal.
                tto_office_emails: r => (
                  Array.isArray(r.tto_office_emails) && r.tto_office_emails.length
                    ? <>{r.tto_office_emails.join(', ')}</>
                    : <span className="text-grayscale-1100">—</span>
                ),
              }} />
          </Section>
          <Section title={`Live mapping — ${live.length} universities in the corpus`}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              Grouped straight from <span className="font-mono">technologies.utech_university_id</span>{' '}
              and <span className="font-mono">university_name</span>. This is the authority;
              anything in code that disagrees is drift.
            </p>
            <DataTable rows={live} columns={MAPPING_COLS} searchable csvName="university_mapping"
              maxHeight="34rem"
              labels={{ code: 'UTECH code', short_code: 'Short', tech_managers: 'Tech managers',
                distinct_tto_emails: 'TTO emails', last_updated: 'Last updated' }}
              empty="No university codes found in the corpus."
              render={{
                code: r => <span className="font-mono">{r.code}</span>,
                last_updated: r => <>{fmtDate(r.last_updated)}</>,
              }} />
          </Section>
          <Section title="Technologies per university">
            <BarList items={live.map(u => ({
              label: `${u.short_code} · ${u.name}`,
              value: Number(u.technologies) || 0,
            }))} />
          </Section>
          <Section title={`Hardcoded copy — combined_rag_system.py (${hardcoded.length})`}
            right={<Btn small onClick={() => setShowCode(v => !v)}>
              {showCode ? 'Hide' : 'Show'}
            </Btn>}>
            <p className="text-xs text-grayscale-1100 -mt-1">
              The name → short-code dictionary the older RAG path resolves university
              names with — the same codes without the <span className="font-mono">UTECH-</span>
              prefix. It is a copy, not a source: edits here must be made in code.
            </p>
            {d.hardcoded_error && <Msg kind="error">{d.hardcoded_error}</Msg>}
            {showCode && (
              <DataTable rows={hardcoded} columns={['short_code', 'name']} searchable
                csvName="university_codes_hardcoded" maxHeight="26rem"
                labels={{ short_code: 'Short code', name: 'Name (as matched)' }}
                empty="The hardcoded map could not be read."
                render={{ short_code: r => <span className="font-mono">{r.short_code}</span> }} />
            )}
          </Section>
        </>
      )}
    </div>
  );
};

export default DatabasePanel;
